import React from "react";
import { Input, Select } from "antd";
import {
  SearchOutlined,
  PictureOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import CommonTable from "../../Components/CustomTable/CustomTable";
import {
  SubmissionHistoryItem,
  SubmissionStatus,
} from "../../Services/SubmissionHistoryService/SubmissionHistoryApi";
import "./SubHistoryTable.css";

// CommonTable keys rows by `id`
export type SubmissionRow = SubmissionHistoryItem & { id: number };

export type StatusFilter = "all" | SubmissionStatus;

interface SubHistoryTableProps {
  data: SubmissionRow[];
  totalCount: number;
  loading?: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (value: StatusFilter) => void;
  onLoadMore?: () => void;
  sectionTitle?: string; // "Costs"
  onViewImage?: (row: SubmissionRow) => void;
  onDownload?: (row: SubmissionRow) => void;
  onRestore?: (row: SubmissionRow) => void;
  onShow?: (row: SubmissionRow) => void;
  onReview?: (row: SubmissionRow) => void;
}

// --- Constants ---
const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All statuses" },
  { value: "Inbox", label: "Inbox" },
  { value: "Archive", label: "Archive" },
  { value: "Deleted", label: "Deleted" },
  { value: "Merged", label: "Merged" },
  { value: "Blocked duplicate", label: "Blocked duplicate" },
];

const pillBase: React.CSSProperties = {
  display: "inline-block",
  padding: "4px 10px",
  borderRadius: 6,
  fontSize: 13,
  background: "#eef0f4",
  color: "#374151",
  border: "1px solid transparent",
};

const statusStyle = (status: SubmissionStatus): React.CSSProperties =>
  status === "Blocked duplicate"
    ? {
        ...pillBase,
        background: "#fff4e5",
        color: "#ea580c",
        border: "1px solid #fdba74",
      }
    : pillBase;

const actionBtnStyle: React.CSSProperties = {
  minWidth: 88,
  padding: "6px 16px",
  borderRadius: 6,
  border: "1px solid #d1d5db",
  background: "#eef0f4",
  color: "#111827",
  fontSize: 13,
  cursor: "pointer",
};

const iconBtnStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  marginRight: 8,
  cursor: "pointer",
  color: "#1f2937",
  fontSize: 16,
};

// Date only. The API sends UTC timestamps without a zone, so mark them as UTC before formatting locally.
const formatDate = (value: string | null) =>
  value
    ? new Date(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";

const SubHistoryTable: React.FC<SubHistoryTableProps> = ({
  data,
  totalCount,
  loading,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  onLoadMore,
  sectionTitle = "Costs",
  onViewImage,
  onDownload,
  onRestore,
  onShow,
  onReview,
}) => {
  // --- Row action (decided by the API from the row's status) ---
  const renderAction = (row: SubmissionRow) => {
    const handler =
      row.action === "Restore" ? onRestore : row.action === "Review" ? onReview : onShow;

    return (
      <button
        type="button"
        style={actionBtnStyle}
        onClick={(e) => {
          e.stopPropagation();
          handler?.(row);
        }}
      >
        {row.action}
      </button>
    );
  };

  // --- Columns ---
  const columns = [
    {
      key: "files",
      title: "",
      width: 70,
      showSortArrows: false,
      render: (_: any, row: SubmissionRow) => (
        <span style={{ whiteSpace: "nowrap" }}>
          <button
            type="button"
            style={iconBtnStyle}
            title="View image"
            onClick={(e) => {
              e.stopPropagation();
              onViewImage?.(row);
            }}
          >
            <PictureOutlined />
          </button>
          <button
            type="button"
            style={iconBtnStyle}
            title="Download"
            onClick={(e) => {
              e.stopPropagation();
              onDownload?.(row);
            }}
          >
            <DownloadOutlined />
          </button>
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      showSortArrows: false,
      render: (text: SubmissionStatus) => (
        <span style={statusStyle(text)}>{text}</span>
      ),
    },
    {
      title: "Item ID",
      dataIndex: "invoiceId",
      key: "itemId",
      showSortArrows: false,
    },
    {
      title: "Submitted at",
      dataIndex: "submittedAt",
      key: "submittedAt",
      showSortArrows: false,
      render: (text: string | null) => formatDate(text),
    },
    {
      title: "Submitted by",
      dataIndex: "submittedBy",
      key: "submittedBy",
      showSortArrows: false,
      render: (text: string | null) => text || "-",
    },
    {
      title: "Submission method",
      dataIndex: "submissionMethod",
      key: "submissionMethod",
      showSortArrows: false,
      render: (text: string | null) => text || "-",
    },
    {
      title: "Supplier",
      dataIndex: "supplier",
      key: "supplier",
      showSortArrows: false,
      render: (text: string | null) => text || "-",
    },
    {
      title: "Total",
      dataIndex: "totalAmount",
      key: "total", // "total" is in CommonTable's rightAlignedKeys
      showSortArrows: false,
      render: (value: number | null, row: SubmissionRow) =>
        value != null
          ? `${row.currency ? `${row.currency} ` : ""}${value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          : "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      showSortArrows: false,
      // NOTE: not using isActions — that flag is the built-in Edit/Delete menu
      render: (_: any, row: SubmissionRow) => renderAction(row),
    },
  ];

  return (
    <div className="SubHistoryTable">
      <div className="SubHistoryTable__top">
        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 16 }}>
          Submission History
        </h1>

        <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
          <Input
            size="large"
            style={{ flex: 1 }}
            prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            placeholder="Search by Item ID, supplier or submitted by"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            allowClear
          />
          <Select
            size="large"
            style={{ width: 220 }}
            value={statusFilter}
            onChange={onStatusFilterChange}
            options={STATUS_OPTIONS}
          />
        </div>

        <h3 style={{ fontWeight: 600, marginBottom: 8 }}>{sectionTitle}</h3>
      </div>

      {data.length === 0 ? (
        // CommonTable's own empty text is inbox-specific
        !loading && <p className="SubHistoryTable__empty">No submissions found.</p>
      ) : (
        <div className="custom-table-wrapper">
          <CommonTable
            data={data}
            columns={columns}
            infinityScroll={!!onLoadMore}
            loadMoreInvoicesOnScroll={onLoadMore}
            totalCount={totalCount}
          />
        </div>
      )}
    </div>
  );
};

export default SubHistoryTable;
